import { motion } from "framer-motion";
import {
  Layers, Sparkles, FileCheck2, Compass, Search, Palette, Heart,
  TrendingUp, ClipboardList, Brain, ListChecks, Repeat2, SplitSquareHorizontal,
  Shuffle, GitCompareArrows, BadgeCheck, FlaskConical, Microscope, ShieldCheck,
  ScanLine, Rocket, CheckCircle2, XCircle, Cpu,
} from "lucide-react";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import { ValidationBanner } from "@/components/science/ValidationBanner";
import { cn } from "@/lib/utils";
import {
  FRAMEWORKS, BLUEPRINT_GROUPS, ITEM_FORMATS, PROOF_STAGES, DELIVERABLE_SECTIONS, SCIENCE_STATS, APTITUDE_TEST_TYPES,
} from "@/data/scienceContent";

const GROUP_ICONS = [Compass, Brain, Search, Palette, Heart, TrendingUp, ClipboardList];
const GROUP_GRADIENTS = [
  "from-indigo-500 to-violet-500", "from-cyan-500 to-blue-500", "from-fuchsia-500 to-pink-500",
  "from-emerald-500 to-teal-500", "from-amber-500 to-orange-500", "from-rose-500 to-red-500",
  "from-blue-500 to-indigo-500",
];
const FRAMEWORK_ICONS = [Compass, Layers, Palette, Brain, TrendingUp, Heart, ScanLine, ShieldCheck, ListChecks, Rocket, Search, Microscope];
const ITEM_FORMAT_ICONS = [ListChecks, Repeat2, SplitSquareHorizontal, Shuffle, GitCompareArrows, BadgeCheck];
const APTITUDE_ICONS = [Cpu, ListChecks, Layers, Compass, Repeat2, ScanLine, Sparkles, Brain];

const PROOF_STATUS_STYLES = {
  Complete: { pill: "bg-emerald-50 text-emerald-700 border-emerald-200", badge: "bg-emerald-600", ring: "ring-emerald-200" },
  "In Progress": { pill: "bg-amber-50 text-amber-700 border-amber-200", badge: "bg-amber-500", ring: "ring-amber-200" },
  "Not Started Yet": { pill: "bg-slate-100 text-slate-500 border-slate-200", badge: "bg-slate-300", ring: "ring-slate-100" },
};

const fadeUp = {
  initial: { opacity: 0, y: 22 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.5, ease: "easeOut" },
};

const stagger = (i) => ({
  initial: { opacity: 0, y: 18 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-40px" },
  transition: { duration: 0.45, ease: "easeOut", delay: i * 0.06 },
});

function SectionHeading({ eyebrow, title, subtitle, light = false }) {
  return (
    <motion.div {...fadeUp} className="max-w-3xl mx-auto text-center mb-14">
      <span
        className={cn(
          "inline-flex items-center gap-1.5 text-xs font-mono font-semibold uppercase tracking-[0.25em] px-3 py-1 rounded-full border mb-4",
          light ? "text-cyan-300 bg-cyan-500/10 border-cyan-500/30" : "text-indigo-600 bg-indigo-50 border-indigo-200/70"
        )}
      >
        <Sparkles className="h-3 w-3" /> {eyebrow}
      </span>
      <h2
        className={cn(
          "font-heading text-2xl sm:text-3xl font-extrabold tracking-tight mb-4",
          light ? "text-white" : "text-slate-900"
        )}
      >
        {title}
      </h2>
      {subtitle && (
        <p className={cn("text-base leading-relaxed", light ? "text-slate-300" : "text-slate-600")}>{subtitle}</p>
      )}
    </motion.div>
  );
}

export default function ScienceDeepDive() {
  return (
    <div className="min-h-screen bg-white" data-testid="science-deep-dive-page">
      <Navbar />

      <section className="relative overflow-hidden bg-gradient-to-br from-[#0B0F1E] via-[#141C38] to-[#0B0F1E] py-24 px-6">
        <div className="absolute -top-32 -left-24 h-96 w-96 rounded-full bg-indigo-600/25 blur-[110px]" />
        <div className="absolute -bottom-32 -right-24 h-96 w-96 rounded-full bg-cyan-500/20 blur-[110px]" />
        <div className="absolute inset-0 opacity-[0.12] [background-image:radial-gradient(circle_at_1px_1px,#818CF8_1px,transparent_0)] [background-size:32px_32px]" />
        <motion.div {...fadeUp} className="max-w-4xl mx-auto text-center relative">
          <span className="inline-flex items-center gap-2 text-xs font-mono font-semibold uppercase tracking-[0.25em] text-cyan-300 bg-cyan-500/10 border border-cyan-500/30 px-4 py-1.5 rounded-full mb-6">
            <FlaskConical className="h-3.5 w-3.5" /> Scientific Foundation &amp; Rigor
          </span>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-[1.08] mb-6 text-white">
            What Makes CareerStudy360{" "}
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-indigo-400 via-violet-300 to-cyan-300">
              Different From a Typical Quiz
            </span>
          </h1>
          <p className="text-base sm:text-lg text-slate-400 leading-relaxed max-w-2xl mx-auto">
            A whole-person instrument spanning interests, personality, aptitude, values, strengths, leadership and
            entrepreneurial potential — synthesised from established behavioural science, not a single 10-minute quiz.
          </p>
        </motion.div>
      </section>

      <section className="py-16 px-6 bg-gradient-to-b from-amber-50/60 to-white">
        <motion.div {...fadeUp} className="max-w-4xl mx-auto">
          <div className="relative rounded-2xl p-[1.5px] bg-gradient-to-r from-amber-300 via-amber-200 to-amber-300 shadow-lg shadow-amber-100/60">
            <ValidationBanner className="border-0" />
          </div>
        </motion.div>
      </section>

      <section className="py-8 px-6 bg-slate-50">
        <div className="max-w-6xl mx-auto">
          <SectionHeading
            eyebrow="Scientific Foundation"
            title="Built on Established Frameworks — Not Invented Overnight"
            subtitle="Every one of the 40 dimensions traces back to a named, published body of behavioural-science research. CareerStudy360 synthesises these into one unified instrument; it does not reproduce or claim to be any of them."
          />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 max-w-4xl mx-auto">
            {FRAMEWORKS.map((f, i) => {
              const Icon = FRAMEWORK_ICONS[i % FRAMEWORK_ICONS.length];
              return (
                <motion.div
                  key={f}
                  {...stagger(i)}
                  data-testid={`framework-chip-${f.slice(0, 8)}`}
                  className="group relative rounded-xl border border-slate-200 bg-white px-4 py-3.5 text-sm text-slate-700 flex items-center gap-3 hover:border-indigo-300 hover:shadow-md hover:-translate-y-0.5 transition-all duration-300"
                >
                  <span className="h-8 w-8 rounded-lg bg-indigo-50 flex items-center justify-center shrink-0 group-hover:bg-indigo-100 transition-colors">
                    <Icon className="h-4 w-4 text-indigo-600" />
                  </span>
                  {f}
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <SectionHeading
            eyebrow="The Blueprint"
            title="40 Dimensions Across 7 Integrated Layers"
            subtitle="Each layer is measured independently, then combined into higher-order potentials and career-match scores."
          />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {BLUEPRINT_GROUPS.map((g, i) => {
              const Icon = GROUP_ICONS[i] || Layers;
              const gradient = GROUP_GRADIENTS[i % GROUP_GRADIENTS.length];
              const big = i === 0;
              return (
                <motion.div
                  key={g.label}
                  {...stagger(i)}
                  data-testid={`blueprint-group-${i}`}
                  className={cn(
                    "group relative rounded-2xl border border-slate-200 p-6 text-center overflow-hidden hover:border-transparent hover:shadow-xl transition-all duration-300",
                    big && "sm:col-span-2"
                  )}
                >
                  <div className={cn("absolute inset-0 opacity-0 group-hover:opacity-[0.06] bg-gradient-to-br transition-opacity duration-300", gradient)} />
                  <span
                    className={cn(
                      "relative h-12 w-12 rounded-2xl flex items-center justify-center mx-auto mb-4 bg-gradient-to-br shadow-lg transition-transform duration-300 group-hover:rotate-6",
                      gradient
                    )}
                  >
                    <Icon className="h-5 w-5 text-white" />
                  </span>
                  <p className="relative text-sm font-semibold text-slate-900 mb-1.5">{g.label}</p>
                  <span className="relative inline-flex items-center gap-1 text-xs font-medium text-slate-400 bg-slate-50 rounded-full px-2.5 py-0.5">
                    {g.count} dimensions
                  </span>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-24 px-6 bg-slate-50">
        <div className="max-w-6xl mx-auto">
          <SectionHeading
            eyebrow="Item Design"
            title="We Ask Every Question in Six Different Ways"
            subtitle="Mixing formats means someone trying to 'game' one item type rarely games all six consistently — improving how honestly the profile reflects the real you."
          />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {ITEM_FORMATS.map((f, i) => {
              const Icon = ITEM_FORMAT_ICONS[i % ITEM_FORMAT_ICONS.length];
              return (
                <motion.div
                  key={f.name}
                  {...stagger(i)}
                  data-testid={`item-format-${f.name.slice(0, 8)}`}
                  className="group relative rounded-2xl border border-slate-200 bg-white p-5 overflow-hidden hover:border-indigo-300 hover:shadow-lg transition-all duration-300"
                >
                  <div className="flex items-start justify-between mb-3">
                    <span className="h-9 w-9 rounded-xl bg-indigo-50 flex items-center justify-center group-hover:bg-indigo-600 transition-colors duration-300">
                      <Icon className="h-4.5 w-4.5 text-indigo-600 group-hover:text-white transition-colors duration-300" />
                    </span>
                    <span className="font-mono text-[11px] font-bold text-slate-300 group-hover:text-indigo-300 transition-colors">
                      0{i + 1}
                    </span>
                  </div>
                  <p className="text-sm font-semibold text-slate-900 mb-1.5">{f.name}</p>
                  <p className="text-xs text-slate-500 leading-relaxed">{f.detail}</p>
                  <div className="absolute bottom-0 left-0 h-0.5 w-0 bg-gradient-to-r from-indigo-500 to-cyan-400 group-hover:w-full transition-all duration-500" />
                </motion.div>
              );
            })}
          </div>
          <motion.p {...fadeUp} className="text-xs text-slate-400 text-center mt-8 max-w-2xl mx-auto">
            A handful of built-in quality and consistency checks also run silently in the background throughout the
            assessment — never shown during the test — and roll up into the single "Assessment Confidence" rating
            you see on your report.
          </motion.p>
        </div>
      </section>

      <section className="py-24 px-6 bg-gradient-to-br from-violet-950 via-[#1E1B4B] to-slate-950 text-white">
        <div className="max-w-6xl mx-auto">
          <SectionHeading
            eyebrow="Beyond Self-Report"
            title="The Aptitude Test — 8 Performance-Based Exercises"
            subtitle="Inspired by aptitude batteries like YouScience, this optional module reinforces your report with objective reasoning, speed and ideation data — not just how you describe yourself."
            light
          />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {APTITUDE_TEST_TYPES.map((t, i) => {
              const Icon = APTITUDE_ICONS[i % APTITUDE_ICONS.length];
              return (
                <motion.div
                  key={t.name}
                  {...stagger(i)}
                  data-testid={`aptitude-type-card-${t.name.slice(0, 8)}`}
                  className="rounded-2xl border border-white/10 bg-white/5 p-5 hover:border-cyan-400/40 hover:bg-white/10 transition-all duration-300"
                >
                  <span className="h-9 w-9 rounded-xl bg-cyan-500/15 flex items-center justify-center mb-3">
                    <Icon className="h-4.5 w-4.5 text-cyan-300" />
                  </span>
                  <p className="text-sm font-semibold text-white mb-1.5">{t.name}</p>
                  <p className="text-xs text-slate-400 leading-relaxed">{t.detail}</p>
                </motion.div>
              );
            })}
          </div>
          <motion.p {...fadeUp} className="text-xs text-slate-400 text-center mt-8 max-w-2xl mx-auto">
            The 6 reasoning/speed subtests blend into your Cognitive & Professional Aptitude scores (60% existing
            score / 40% Aptitude Test), while Idea Generation and Work Approach Style add qualitative context to
            your AI narrative — reinforcing your report, never overriding it.
          </motion.p>
        </div>
      </section>

      <section className="py-24 px-6">
        <div className="max-w-5xl mx-auto">
          <SectionHeading
            eyebrow="The Proof Process"
            title="The 8-Stage Standard We Hold Ourselves To"
            subtitle="This is the validation pipeline CareerStudy360 is running toward — the same standard used by established, peer-reviewed psychometric instruments. Status below reflects where we genuinely stand today, updated as real pilot data comes in."
          />
          <div className="relative">
            <div className="hidden lg:block absolute top-[22px] left-[6%] right-[6%] h-0.5 bg-gradient-to-r from-emerald-400 via-amber-400 to-slate-200" />
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-10">
              {PROOF_STAGES.map((s, i) => {
                const style = PROOF_STATUS_STYLES[s.status];
                return (
                  <motion.div key={s.step} {...stagger(i)} data-testid={`proof-stage-${s.step}`} className="relative">
                    <div className="flex items-center gap-2 mb-3">
                      <span
                        className={cn(
                          "relative z-10 h-11 w-11 rounded-full text-white flex items-center justify-center font-mono text-xs font-bold shrink-0 ring-4 ring-white shadow-md",
                          style.badge
                        )}
                      >
                        {s.step}
                      </span>
                      <span
                        className={cn("text-[10px] font-semibold uppercase tracking-wide rounded-full border px-2 py-0.5", style.pill)}
                        data-testid={`proof-stage-status-${s.step}`}
                      >
                        {s.status}
                      </span>
                    </div>
                    <p className="text-sm font-semibold text-slate-900 mb-1.5">{s.name}</p>
                    <p className="text-xs text-slate-500 leading-relaxed">{s.detail}</p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden py-24 px-6 bg-gradient-to-br from-slate-950 via-[#0B1224] to-slate-950 text-white">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-[420px] w-[420px] rounded-full border border-cyan-500/10 animate-pulse" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-[280px] w-[280px] rounded-full border border-indigo-500/10" />
        <div className="max-w-4xl mx-auto text-center relative">
          <motion.div {...fadeUp}>
            <span className="inline-flex h-14 w-14 rounded-2xl bg-gradient-to-br from-indigo-500 to-cyan-400 items-center justify-center mx-auto mb-6 shadow-lg shadow-cyan-500/30">
              <Cpu className="h-6 w-6 text-white" />
            </span>
            <h2 className="font-heading text-2xl sm:text-3xl font-extrabold mb-5">Where AI Fits — and Where It Doesn't</h2>
            <p className="text-slate-300 leading-relaxed max-w-2xl mx-auto mb-10">
              Every score in your report comes from a deterministic scoring engine applied consistently to every
              student — not from an AI "reading" your answers by intuition. AI is used only as an interpretation
              layer: it explains, in plain language, what the already-computed numbers mean for you. It never assigns
              or overrides a psychometric score.
            </p>
          </motion.div>
          <div className="grid sm:grid-cols-2 gap-4 max-w-2xl mx-auto text-left">
            <motion.div {...fadeUp} className="rounded-2xl border border-emerald-500/25 bg-emerald-500/5 p-5">
              <CheckCircle2 className="h-5 w-5 text-emerald-400 mb-3" />
              <p className="text-sm font-semibold text-white mb-1">What AI Does</p>
              <p className="text-xs text-slate-400 leading-relaxed">Narrates already-computed scores in warm, plain language for you to understand.</p>
            </motion.div>
            <motion.div {...fadeUp} className="rounded-2xl border border-rose-500/25 bg-rose-500/5 p-5">
              <XCircle className="h-5 w-5 text-rose-400 mb-3" />
              <p className="text-sm font-semibold text-white mb-1">What AI Never Does</p>
              <p className="text-xs text-slate-400 leading-relaxed">Assign, alter or override any psychometric score, ranking or match percentage.</p>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-24 px-6">
        <div className="max-w-4xl mx-auto">
          <SectionHeading eyebrow="The Deliverable" title="What You Actually Receive" />
          <div className="grid sm:grid-cols-2 gap-3">
            {DELIVERABLE_SECTIONS.map((d, i) => (
              <motion.div
                key={d}
                {...stagger(i)}
                data-testid={`deliverable-item-${d.slice(0, 8)}`}
                className="flex items-start gap-3 text-sm text-slate-700 rounded-xl border border-slate-100 bg-slate-50/60 p-3.5 hover:bg-emerald-50/60 hover:border-emerald-200 transition-colors duration-300"
              >
                <span className="h-6 w-6 rounded-full bg-emerald-100 flex items-center justify-center shrink-0 shadow-sm shadow-emerald-100">
                  <FileCheck2 className="h-3.5 w-3.5 text-emerald-600" />
                </span>
                {d}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 px-6 bg-slate-50 border-t border-slate-100">
        <div className="max-w-5xl mx-auto grid sm:grid-cols-5 gap-6 text-center mb-14">
          {SCIENCE_STATS.map((s, i) => (
            <motion.div
              key={s.label}
              {...stagger(i)}
              data-testid={`science-stat-${s.label.slice(0, 8)}`}
              className="rounded-2xl border border-slate-200 bg-white py-6 px-3 hover:border-indigo-200 hover:shadow-md transition-all duration-300"
            >
              <p className="font-heading text-3xl font-black bg-clip-text text-transparent bg-gradient-to-r from-indigo-600 via-violet-600 to-cyan-500">
                {s.value}
              </p>
              <p className="text-xs text-slate-500 mt-1">{s.label}</p>
            </motion.div>
          ))}
        </div>
        <div className="max-w-3xl mx-auto">
          <div className="relative rounded-2xl p-[1.5px] bg-gradient-to-r from-amber-300 via-amber-200 to-amber-300 shadow-lg shadow-amber-100/60">
            <ValidationBanner className="border-0" />
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
